/**
 * Analytics Service
 * Manages PostHog analytics integration with session recording
 */

import posthog from 'posthog-js';

class AnalyticsService {
  private initialized = false;

  /**
   * Initialize PostHog analytics
   * Call this once at app startup
   */
  initialize(): void {
    // Don't initialize in test environments
    if (import.meta.env.MODE === 'test') {
      console.log('Analytics disabled in test environment');
      return;
    }

    const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
    const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

    // Only initialize if we have the required credentials
    if (!posthogKey) {
      console.warn('PostHog key not found. Analytics will be disabled.');
      return;
    }

    try {
      posthog.init(posthogKey, {
        api_host: posthogHost || 'https://us.i.posthog.com',
        // Enable session recording
        session_recording: {
          enabled: true,
          // Record console logs for better debugging
          recordConsole: true,
          // Record network requests (optional, disable if privacy concern)
          recordNetworkRequests: true,
        },
        // Capture pageviews automatically
        capture_pageview: true,
        // Capture page leave events
        capture_pageleave: true,
        // Enable autocapture for click events
        autocapture: true,
        // Disable in development unless explicitly enabled
        disable_session_recording: import.meta.env.DEV && !import.meta.env.VITE_ENABLE_ANALYTICS_IN_DEV,
        // Respect user privacy
        respect_dnt: true,
        // Enable performance monitoring
        enable_recording_console_log: true,
      });

      this.initialized = true;
      console.log('PostHog analytics initialized with session recording enabled');
    } catch (error) {
      console.error('Failed to initialize PostHog:', error);
    }
  }

  /**
   * Track a custom event
   */
  trackEvent(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.initialized) return;

    try {
      posthog.capture(eventName, properties);
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  /**
   * Identify a user (optional - use if you have user IDs)
   */
  identifyUser(userId: string, properties?: Record<string, unknown>): void {
    if (!this.initialized) return;

    try {
      posthog.identify(userId, properties);
    } catch (error) {
      console.error('Failed to identify user:', error);
    }
  }

  /**
   * Start a new session recording
   */
  startRecording(): void {
    if (!this.initialized) return;

    try {
      posthog.startSessionRecording();
    } catch (error) {
      console.error('Failed to start session recording:', error);
    }
  }

  /**
   * Stop the current session recording
   */
  stopRecording(): void {
    if (!this.initialized) return;

    try {
      posthog.stopSessionRecording();
    } catch (error) {
      console.error('Failed to stop session recording:', error);
    }
  }

  /**
   * Check if analytics is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Reset analytics (e.g., on logout)
   */
  reset(): void {
    if (!this.initialized) return;

    try {
      posthog.reset();
    } catch (error) {
      console.error('Failed to reset analytics:', error);
    }
  }
}

// Export a singleton instance
export const analyticsService = new AnalyticsService();
